import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Component as ComponentIcon, CheckCircle2, Hash, Image as ImageIcon, Ruler, PieChart, Grid3x3, ArrowLeftRight, Table as TableIcon, Lightbulb, Coins, Columns, Clock, Grid2x2, BookOpen, Scissors, SquareStack, Circle, Crosshair, Shapes, BoxIcon, BarChart3, Percent, GitBranch, Map as MapIcon, Compass, LayoutGrid, Gauge, Globe, Thermometer, Users, FileText, Calendar, TrendingUp, Timer, EyeOff, HelpCircle } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Question } from '../../../../content';
import { VISUAL_REGISTRY } from '../../../../visuals';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
import { VISUAL_CATEGORIES, useRegistryFilters, RegistryToolbar, CategorySection, ExplorerItem } from './registryExplorer';
const VISUAL_ICONS: Record<string, React.ElementType> = {
  stairs: Ruler,
  fraction: PieChart,
  geo: Grid3x3,
  numberline: ArrowLeftRight,
  ratio: TableIcon,
  rule: Lightbulb,
  money: Coins,
  column: Columns,
  clock: Clock,
  verbTable: BookOpen,
  kofschipMnemonic: Scissors,
  syllableBlocks: SquareStack,
  circle: Circle,
  coordinateGrid: Crosshair,
  box3d: BoxIcon,
  compositeShape: Shapes,
  geometricShape: Shapes,
  barChart: BarChart3,
  percentageBar: Percent,
  fractionStrip: Columns,
  splitDiagram: GitBranch,
  sentenceDiagram: GitBranch,
  mapScale: MapIcon,
  protractor: Compass,
  symmetryGrid: Grid3x3,
  numberBoard: LayoutGrid,
  speedDiagram: Gauge,
  priceCompare: BarChart3,
  timeline: Clock,
  worldClockPanel: Globe,
  thermometer: Thermometer,
  ruler: Ruler,
  measuringCup: BarChart3,
  weighScale: BarChart3,
  equalSharing: Users,
  readingPassage: FileText,
  calendar: Calendar,
  lineChart: TrendingUp,
  digitalClock: Timer
};
const VISUAL_DESCRIPTIONS: Record<string, string> = {
  stairs: 'Metriek stelsel trap voor omrekenen',
  fraction: 'Breuken cirkel of rechthoek',
  geo: 'Geometrisch raster voor oppervlakte/omtrek',
  numberline: 'Getallenlijn voor positioneren en sprongen',
  ratio: 'Verhoudingstabel',
  rule: 'Regelkaart met uitleg',
  money: 'Geldmunten en biljetten',
  column: 'Cijferend rekenen kolommen',
  clock: 'Analoge klok',
  verbTable: 'Werkwoordspelling tabel',
  kofschipMnemonic: "'t Kofschip ezelsbruggetje",
  syllableBlocks: 'Lettergrepen blokken',
  circle: 'Cirkel met straal/diameter',
  coordinateGrid: 'Assenstelsel met coördinaten',
  box3d: '3D kubus of balk',
  compositeShape: 'Samengestelde 2D vorm',
  geometricShape: 'Basis 2D vorm',
  barChart: 'Staafdiagram',
  percentageBar: 'Percentagebalk (0-100%)',
  fractionStrip: 'Breukenstrook',
  splitDiagram: 'Splitsingsschema',
  sentenceDiagram: 'Zinsontleding schema',
  mapScale: 'Kaartschaal',
  protractor: 'Geodriehoek / gradenboog',
  symmetryGrid: 'Symmetrie raster',
  numberBoard: 'Honderdveld / getallenbord',
  speedDiagram: 'Snelheidsmeter / diagram',
  priceCompare: 'Prijsvergelijking weegschaal',
  timeline: 'Tijdlijn',
  worldClockPanel: 'Wereldklokken paneel',
  thermometer: 'Thermometer',
  ruler: 'Liniaal',
  measuringCup: 'Maatbeker',
  weighScale: 'Weegschaal',
  equalSharing: 'Eerlijk verdelen (poppetjes)',
  readingPassage: 'Leestekst',
  calendar: 'Kalender',
  lineChart: 'Lijngrafiek',
  digitalClock: 'Digitale klok'
};
interface VisualsTabProps {
  questions: Question[];
  onSelectVisual: (visualType: string) => void;
}
// Categories typically used as a 'support'-only fallback — collapse by default
// so admins land on the more diagnostic, varied visuals first.
const VISUALS_DEFAULT_COLLAPSED = new Set<string>(['rules']);
export function VisualsTab({
  questions,
  onSelectVisual
}: VisualsTabProps) {
  const registeredVisuals = useMemo(() => Object.keys(VISUAL_REGISTRY).sort(), []);
  // Build items list
  const allItems = useMemo(() => {
    const itemsMap = new Map<string, ExplorerItem>();
    registeredVisuals.forEach(v => {
      itemsMap.set(v, {
        id: v,
        label: v,
        description: VISUAL_DESCRIPTIONS[v] || 'Geen beschrijving beschikbaar',
        icon: VISUAL_ICONS[v] || ImageIcon,
        usageCount: 0,
        modules: new Set(),
        isRegistered: true,
        stimulusCount: 0,
        supportCount: 0
      });
    });
    questions.forEach(q => {
      const moduleCode = q.familyId?.split('-')[0] || 'UNK';
      q.stimulusAids?.forEach(aid => {
        if (aid.type) {
          if (!itemsMap.has(aid.type)) {
            itemsMap.set(aid.type, {
              id: aid.type,
              label: aid.type,
              description: 'Onbekende visual',
              icon: HelpCircle,
              usageCount: 0,
              modules: new Set(),
              isRegistered: false,
              stimulusCount: 0,
              supportCount: 0
            });
          }
          const item = itemsMap.get(aid.type)!;
          item.usageCount++;
          item.stimulusCount!++;
          item.modules.add(moduleCode);
        }
      });
      q.supportAids?.forEach(aid => {
        if (aid.type) {
          if (!itemsMap.has(aid.type)) {
            itemsMap.set(aid.type, {
              id: aid.type,
              label: aid.type,
              description: 'Onbekende visual',
              icon: HelpCircle,
              usageCount: 0,
              modules: new Set(),
              isRegistered: false,
              stimulusCount: 0,
              supportCount: 0
            });
          }
          const item = itemsMap.get(aid.type)!;
          item.usageCount++;
          item.supportCount!++;
          item.modules.add(moduleCode);
        }
      });
    });
    return Array.from(itemsMap.values()).sort((a, b) => b.usageCount - a.usageCount);
  }, [registeredVisuals, questions]);
  // Top-2 categories by usage, minus the ones we always collapse
  const defaultExpandedIds = useMemo(() => {
    const ranked = VISUAL_CATEGORIES.map(cat => {
      const total = cat.items.reduce((sum, id) => {
        const it = allItems.find(x => x.id === id);
        return sum + (it?.usageCount ?? 0);
      }, 0);
      return {
        id: cat.id,
        total
      };
    }).filter(c => !VISUALS_DEFAULT_COLLAPSED.has(c.id)).sort((a, b) => b.total - a.total);
    return ranked.slice(0, 2).map(c => c.id);
  }, [allItems]);
  const {
    filters,
    setFilters,
    viewMode,
    setViewMode,
    sortMode,
    setSortMode,
    filteredItems,
    availableModules,
    hasActiveFilters,
    clearFilters,
    toggleCategory,
    expandAll,
    collapseAll,
    isCategoryExpanded
  } = useRegistryFilters(allItems, {
    storageKey: 'admin.visuals',
    defaultExpandedIds
  });
  // Stats
  const activeVisuals = allItems.filter(v => v.usageCount > 0).length;
  const unusedVisuals = allItems.filter(v => v.usageCount === 0 && v.isRegistered).length;
  const totalReferences = allItems.reduce((sum, u) => sum + u.usageCount, 0);
  // Group filtered items by category
  const groupedItems = useMemo(() => {
    const grouped = new Map<string, ExplorerItem[]>();
    const mappedIds = new Set<string>();
    VISUAL_CATEGORIES.forEach(cat => {
      const catItems = filteredItems.filter(item => cat.items.includes(item.id));
      grouped.set(cat.id, catItems);
      catItems.forEach(item => mappedIds.add(item.id));
    });
    const uncategorized = filteredItems.filter(item => !mappedIds.has(item.id) && item.usageCount > 0);
    if (uncategorized.length > 0) {
      grouped.set('uncategorized', uncategorized);
    }
    const unused = filteredItems.filter(item => item.usageCount === 0);
    if (unused.length > 0) {
      grouped.set('unused', unused);
    }
    return grouped;
  }, [filteredItems]);
  const allVisibleCategoryIds = useMemo(() => {
    const ids: string[] = [];
    VISUAL_CATEGORIES.forEach(cat => {
      if ((groupedItems.get(cat.id) ?? []).length > 0) ids.push(cat.id);
    });
    if (groupedItems.has('uncategorized')) ids.push('uncategorized');
    if (groupedItems.has('unused')) ids.push('unused');
    return ids;
  }, [groupedItems]);
  const renderCardContent = (item: ExplorerItem) => {
    const total = item.usageCount;
    if (total === 0) return null;
    const stimPct = (item.stimulusCount || 0) / total * 100;
    const suppPct = (item.supportCount || 0) / total * 100;
    return <div className="space-y-1.5" data-id="element-5762">
        <div className="flex justify-between text-[10px] text-gray-500" data-id="element-5763">
          <span data-id="element-5764">Stimulus ({item.stimulusCount})</span>
          <span data-id="element-5765">Support ({item.supportCount})</span>
        </div>
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex" data-id="element-5766">
          <div className="bg-purple-400 h-full" style={{
          width: `${stimPct}%`
        }} data-id="element-5767" />
          <div className="bg-amber-400 h-full" style={{
          width: `${suppPct}%`
        }} data-id="element-5768" />
        </div>
      </div>;
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-5769">
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4" data-id="element-5770">
        <motion.div variants={itemVariants} data-id="element-5771">
          <StatCard label="Geregistreerde Visuals" value={registeredVisuals.length} icon={<ComponentIcon className="w-5 h-5" data-id="element-5773" />} color="purple" data-id="element-5772" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5774">
          <StatCard label="Actief in Gebruik" value={activeVisuals} icon={<CheckCircle2 className="w-5 h-5" data-id="element-5776" />} color="emerald" data-id="element-5775" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5777">
          <StatCard label="Ongebruikte Visuals" value={unusedVisuals} icon={<EyeOff className="w-5 h-5" data-id="element-5779" />} color="gray" data-id="element-5778" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5780">
          <StatCard label="Totale Referenties" value={totalReferences.toLocaleString('nl-NL')} icon={<Hash className="w-5 h-5" data-id="element-5782" />} color="blue" data-id="element-5781" />
        </motion.div>
      </motion.div>

      <RegistryToolbar title="Visuals Explorer" icon={<ComponentIcon className="w-5 h-5 text-purple-500" data-id="element-5784" />} activeCount={activeVisuals} totalCount={registeredVisuals.length} filters={filters} onFiltersChange={setFilters} viewMode={viewMode} onViewModeChange={setViewMode} availableModules={availableModules} accentColor="purple" sortMode={sortMode} onSortModeChange={setSortMode} onExpandAll={() => expandAll(allVisibleCategoryIds)} onCollapseAll={collapseAll} hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters} filteredCount={filteredItems.length} data-id="element-5783" />

      <div className="space-y-4 sm:space-y-6" data-id="element-5785">
        {VISUAL_CATEGORIES.map(category => {
        const items = groupedItems.get(category.id) || [];
        if (items.length === 0) return null;
        return <CategorySection key={category.id} category={category} items={items} viewMode={viewMode} onSelectItem={onSelectVisual} isExpanded={isCategoryExpanded(category.id)} onToggleExpanded={() => toggleCategory(category.id)} accentColor="purple" renderCardContent={renderCardContent} data-id="element-5786" />;
      })}

        {groupedItems.has('uncategorized') && <CategorySection category={{
        id: 'uncategorized',
        label: 'Overige Actieve Visuals',
        icon: HelpCircle,
        description: 'Niet ingedeeld in een categorie',
        items: []
      }} items={groupedItems.get('uncategorized')!} viewMode={viewMode} onSelectItem={onSelectVisual} isExpanded={isCategoryExpanded('uncategorized')} onToggleExpanded={() => toggleCategory('uncategorized')} accentColor="purple" renderCardContent={renderCardContent} data-id="element-5787" />}

        {groupedItems.has('unused') && <CategorySection category={{
        id: 'unused',
        label: 'Ongebruikte Visuals',
        icon: EyeOff,
        description: 'Geregistreerd maar niet in gebruik',
        items: []
      }} items={groupedItems.get('unused')!} viewMode={viewMode} onSelectItem={onSelectVisual} isExpanded={isCategoryExpanded('unused')} onToggleExpanded={() => toggleCategory('unused')} accentColor="purple" data-id="element-5788" />}

        {filteredItems.length === 0 && <div className="text-center py-10 sm:py-12 text-sm text-gray-500 bg-white rounded-xl border border-gray-200" data-id="element-5789">
            Geen visuals gevonden die voldoen aan de filters.
          </div>}
      </div>
    </div>;
}